import {
  Box,
  Divider,
  Stack,
  Typography,
} from "@mui/material"
import { useContext, useMemo, useState } from "react"
import ContentDialog from "../../../Dialogs/ContentDialog"
import numbers from "../../../../core/functions/NumberFunctions"
import CoverImageSecurity from "./CoverImageSecurity"
import { CheckoutContext } from "../../../../core/contexts/CheckoutContext"
import { QuantityPicker } from "../../../Inputs/QuantityPicker"
import { useFormContext, useWatch } from "react-hook-form"
import { TSale } from "../../../../core/types/Sale"
import NumberFunctions from "../../../../core/functions/NumberFunctions"
import CheckoutFunctions from "../../../../core/functions/CheckoutFunctions"
import { TicketEventInfo } from "../../Tickets/TicketEventInfo"
import { TProductDelivery } from "../../../../core/types/Product"
import { useTranslation } from "react-i18next"
import LanguageSelector from "../../LanguageSelector"

const CheckoutCoverImage4 = () => {
  const { productPrice, installment, displayCurrency } = useContext(CheckoutContext)
  const { control, setValue } = useFormContext<TSale>()
  const { t } = useTranslation()

  const quantityValue = useWatch({ control, name: "quantity", defaultValue: 1 })
  /*const areaCodeIdValue = useWatch({
    control,
    name: "ticketSectionCodeId",
    defaultValue: "",
  })*/
  const lotCodeIdValue = useWatch({
    control,
    name: "ticketTierCodeId",
    defaultValue: "",
  })

  const [openDescription, setOpenDescription] = useState(false)

  const installmentNumbers = installment?.original?.map(
    (pi) => pi.installmentNumber,
  ) ?? [0]

  const values = useMemo(() => {
    return {
      quantity: quantityValue,
      installment: installment,
      maxInstallment: Math.max(...installmentNumbers),
      installmentLast: installment?.original?.find(
        (pi) => pi.installmentNumber === Math.max(...installmentNumbers),
      ),
      productValue: NumberFunctions.round(
        productPrice?.firstPurchasePrice ?? productPrice?.value ?? 0,
      ),
      value: NumberFunctions.round(
        quantityValue *
          NumberFunctions.round(
            productPrice?.firstPurchasePrice ?? productPrice?.value ?? 0,
          ),
      ),
    }
  }, [quantityValue, productPrice, installment, installmentNumbers])

  return (
    <>
      <Stack direction={"column"} spacing={0} width={"100%"}>
        <Stack
          direction="row"
          spacing={2}
          justifyContent="end"
          alignItems="center"
          sx={{ width: "100%", pt: 2, px: 3 }}
        >
          <LanguageSelector />
        </Stack>
        <Stack direction="column" sx={{ pl: 3, pr: 3, pt: 3 }} width="100%">
          <Stack
            direction="row"
            spacing={2}
            alignContent="center"
            alignItems="center"
            width="100%"
          >
            <Box sx={{ height: 80, width: 80 }}>
              {productPrice?.productImageUrl &&
                productPrice?.productImageUrl !== "" && (
                  <img
                    src={productPrice?.productImageUrl}
                    alt={productPrice?.productName}
                    style={{
                      height: 80,
                      width: 80,
                      objectFit: "cover",
                      borderRadius: "8px",
                    }}
                  />
                )}
            </Box>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignContent="space-between"
              justifyContent="space-between"
              width="100%"
            >
              <Box gap="16px">
                <Typography
                  color="#343948"
                  fontWeight={700}
                  sx={{ fontSize: 16, lineHeight: "20px" }}
                >
                  {productPrice?.productName}
                </Typography>
                <Typography
                  color="#343948"
                  fontWeight={500}
                  sx={{ fontSize: 14, lineHeight: "16px" }}
                >
                  {productPrice?.offerName}
                </Typography>
                <Typography
                  variant="caption"
                  color={{ xs: "#1C52BD", sm: "#707275" }}
                  fontWeight={400}
                  sx={{ fontSize: 12, lineHeight: "16px", cursor: "pointer" }}
                  onClick={() => setOpenDescription(true)}
                >
                  {t("checkout.cover.productDetails")}
                </Typography>
              </Box>
              <Stack spacing={1}>
                {CheckoutFunctions.isAllowsChooseQuantity(productPrice) && (
                  <Box>
                    <QuantityPicker
                      value={quantityValue}
                      onChange={(next) => setValue("quantity", next)}
                      min={1}
                      size="md"
                    />
                  </Box>
                )}
                <Stack
                  direction={{ xs: "row", sm: "column" }}
                  spacing={{ xs: 1, sm: 0 }}
                  alignContent={{ xs: "center", sm: "end" }}
                  alignItems={{ xs: "center", sm: "end" }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignContent="center"
                    alignItems="center"
                  >
                    {(values.installmentLast?.installmentNumber ?? 0) > 1 && (
                      <Typography
                        color="#343948"
                        fontWeight={700}
                        sx={{ fontSize: 16, lineHeight: "20px" }}
                      >
                        {values.installmentLast?.installmentNumber}x
                      </Typography>
                    )}
                    <Typography
                      color="#343948"
                      fontWeight={700}
                      sx={{ fontSize: 16, lineHeight: "20px" }}
                    >
                      {numbers.formatMoneyCurrency(
                        values.installmentLast?.value ?? values.value,
                        displayCurrency,
                      )}
                    </Typography>
                  </Stack>
                  {(values.installmentLast?.installmentNumber ?? 0) > 1 && (
                    <Typography
                      color="#707275"
                      fontWeight={400}
                      sx={{ fontSize: 12, lineHeight: "16px" }}
                    >
                      {t("checkout.cover.orCash", {
                        value: numbers.formatMoneyCurrency(values.value, displayCurrency),
                      })}
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </Stack>
          </Stack>
          {productPrice?.delivery === TProductDelivery.Ticket &&
            lotCodeIdValue && (
              <TicketEventInfo
                areas={productPrice?.areas || []}
                onChangeArea={(code) => setValue("ticketTierCodeId", code)}
                selectedAreaCodeId={lotCodeIdValue || ""}
                eventDate={productPrice?.eventDate}
                eventLocation={productPrice?.eventAddress || t("checkout.cover.eventLocation")}
                eventLocationUrl={productPrice?.eventLocationUrl}
                usedFallbackSection={productPrice?.usedFallbackSection ?? false}
                fallbackMessage={productPrice?.fallbackMessage ?? undefined}
                requireNominalAtCheckout={productPrice?.requireNominalAtCheckout ?? false}
              />
            )}
        </Stack>
        <CoverImageSecurity />
        <Stack sx={{ px: 3, mt: 3.5, mb: 0.5 }}>
          <Divider sx={{ color: "#DCDDDE" }} />
        </Stack>
      </Stack>
      <ContentDialog
        open={openDescription}
        title={productPrice?.productName}
        hasCancel={false}
        onClose={() => setOpenDescription(false)}
      >
        {productPrice?.productDescription}
      </ContentDialog>
    </>
  )
}

export default CheckoutCoverImage4
